import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import type {
  IChatSessionState,
  IChatSessionWithState,
  IChatMessageWithState,
} from './types'
import type { ISourceDocument } from '@/types/chat-session-types'
import {
  loadFromLocalStorage,
  saveToLocalStorage,
  clearLocalStorage,
  isLocalStorageAvailable,
} from './storage'

export function getOrCreateChatSessionStore(storeKey: string) {
  return defineStore(`chat-session-${storeKey}`, () => {
    const state = ref<IChatSessionState>({
      chatbotId: storeKey,
      sessions: {},
      activeSessionId: null,

      isLoading: false,
      isCreatingSession: false,
      isTyping: false,
      isConnected: false,
      error: null,
      isInitialized: false,
      lastLoaded: null,

      persistMessages: true,
      autoScrollToBottom: true,
    })

    const loadPersistedData = () => {
      if (isLocalStorageAvailable()) {
        const persistedData = loadFromLocalStorage(storeKey)
        if (persistedData) {
          // Mark active session as active
          if (
            persistedData.activeSessionId &&
            persistedData.sessions[persistedData.activeSessionId]
          ) {
            const activeSession =
              persistedData.sessions[persistedData.activeSessionId]
            if (activeSession) {
              persistedData.sessions[persistedData.activeSessionId] = {
                ...activeSession,
                isActive: true,
              } as IChatSessionWithState
            }
          }

          state.value.sessions = persistedData.sessions
          state.value.activeSessionId = persistedData.activeSessionId
          state.value.isInitialized =
            Object.keys(persistedData.sessions).length > 0
        }
      }
    }

    loadPersistedData()

    let saveTimeoutId: ReturnType<typeof setTimeout> | null = null
    const debouncedSave = () => {
      if (saveTimeoutId) {
        clearTimeout(saveTimeoutId)
      }
      saveTimeoutId = setTimeout(() => {
        if (state.value.persistMessages && isLocalStorageAvailable()) {
          saveToLocalStorage(
            storeKey,
            state.value.sessions,
            state.value.activeSessionId
          )
        }
      }, 500)
    }

    const immediateSave = () => {
      if (state.value.persistMessages && isLocalStorageAvailable()) {
        saveToLocalStorage(
          storeKey,
          state.value.sessions,
          state.value.activeSessionId
        )
      }
    }

    const storeChatbotId = computed(() => state.value.chatbotId)
    const sessions = computed(() => state.value.sessions)
    const activeSessionId = computed(() => state.value.activeSessionId)
    const isLoading = computed(() => state.value.isLoading)
    const isCreatingSession = computed(() => state.value.isCreatingSession)
    const isTyping = computed(() => state.value.isTyping)
    const isConnected = computed(() => state.value.isConnected)
    const error = computed(() => state.value.error)
    const isInitialized = computed(() => state.value.isInitialized)
    const lastLoaded = computed(() => state.value.lastLoaded)
    const persistMessages = computed(() => state.value.persistMessages)
    const autoScrollToBottom = computed(() => state.value.autoScrollToBottom)

    const sourceDocuments = computed((): ISourceDocument[] => {
      const activeId = state.value.activeSessionId
      const allSessions = Object.values(state.value.sessions)

      const orderedSessions: IChatSessionWithState[] = [
        ...(activeId && state.value.sessions[activeId]
          ? [state.value.sessions[activeId]!]
          : []),
        ...allSessions
          .filter(s => s.sessionId !== activeId)
          .sort((a, b) => b.lastActivity.getTime() - a.lastActivity.getTime()),
      ]

      const seen = new Set<string>()
      const result: ISourceDocument[] = []

      for (const session of orderedSessions) {
        const msgs = [...(session.messages || [])].reverse()
        for (const msg of msgs) {
          for (const doc of msg.sourceDocuments || []) {
            if (
              doc &&
              doc.name &&
              doc.name.trim() !== '' &&
              !seen.has(doc.name)
            ) {
              seen.add(doc.name)
              result.push(doc)
            }
          }
        }
      }

      return result
    })

    const activeSession = computed(() =>
      state.value.activeSessionId
        ? state.value.sessions[state.value.activeSessionId]
        : null
    )
    const messages = computed(() => activeSession.value?.messages || [])
    const historicalMessages = computed(() => {
      const historical: IChatMessageWithState[] = []
      const inactiveSessions = Object.values(state.value.sessions)
        .filter(session => session.sessionId !== state.value.activeSessionId)
        .sort((a, b) => a.lastActivity.getTime() - b.lastActivity.getTime())

      inactiveSessions.forEach(session => {
        if (session.messages && session.messages.length > 0) {
          historical.push(...session.messages)
        }
      })

      return historical
    })
    const allMessages = computed(() => [
      ...historicalMessages.value,
      ...messages.value,
    ])
    const hasMessages = computed(() => allMessages.value.length > 0)
    const hasHistoricalMessages = computed(
      () => historicalMessages.value.length > 0
    )
    const messageCount = computed(() => allMessages.value.length)
    const lastMessage = computed(() =>
      allMessages.value.length > 0
        ? allMessages.value[allMessages.value.length - 1]
        : null
    )
    const sessionList = computed(() => Object.values(state.value.sessions))

    const setSession = (session: IChatSessionWithState): void => {
      state.value.sessions[session.sessionId] = session
      state.value.isInitialized = true
      state.value.error = null
      immediateSave()
    }

    const setActiveSession = (sessionId: string): boolean => {
      if (!state.value.sessions[sessionId]) {
        state.value.error = `Session ${sessionId} not found`
        return false
      }

      if (state.value.activeSessionId) {
        const currentSession = state.value.sessions[state.value.activeSessionId]
        if (currentSession) {
          currentSession.isActive = false
        }
      }

      state.value.activeSessionId = sessionId
      state.value.sessions[sessionId].isActive = true
      state.value.error = null

      immediateSave()
      return true
    }

    const addMessageToSession = (
      sessionId: string,
      message: IChatMessageWithState
    ): void => {
      const session = state.value.sessions[sessionId]
      if (!session) {
        state.value.error = `Session ${sessionId} not found`
        return
      }

      if (!session.messages) {
        session.messages = []
      }

      const existingMessageIndex = session.messages.findIndex(
        msg => msg.id === message.id
      )
      if (existingMessageIndex !== -1) {
        session.messages[existingMessageIndex] = message
      } else {
        session.messages.push(message)
      }
      session.lastActivity = new Date()

      state.value.lastLoaded = new Date()

      debouncedSave()
    }

    const addMessageToActiveSession = (
      message: IChatMessageWithState
    ): void => {
      if (!state.value.activeSessionId) {
        state.value.error = 'No active session'
        return
      }
      addMessageToSession(state.value.activeSessionId, message)
    }

    const updateMessageById = (
      sessionId: string,
      messageId: string,
      updates: Partial<IChatMessageWithState>
    ): void => {
      const session = state.value.sessions[sessionId]
      if (!session) {
        state.value.error = `Session ${sessionId} not found`
        return
      }

      const messageIndex = session.messages.findIndex(
        msg => msg.id === messageId
      )
      if (messageIndex !== -1) {
        const currentMessage = session.messages[messageIndex]
        if (currentMessage) {
          session.messages.splice(messageIndex, 1, {
            ...currentMessage,
            ...updates,
            id: currentMessage.id,
          } as IChatMessageWithState)
          session.lastActivity = new Date()
          state.value.lastLoaded = new Date()

          debouncedSave()
        }
      }
    }

    const setSessionMessages = (
      sessionId: string,
      messages: IChatMessageWithState[]
    ): void => {
      const session = state.value.sessions[sessionId]
      if (!session) {
        state.value.error = `Session ${sessionId} not found`
        return
      }

      session.messages = messages
      if (messages.length > 0) {
        const lastMessage = messages[messages.length - 1]
        if (lastMessage) {
          session.lastActivity = new Date(lastMessage.timestamp)
        }
      }
      state.value.lastLoaded = new Date()
      immediateSave()
    }

    const clearSessionMessages = (sessionId: string): void => {
      const session = state.value.sessions[sessionId]
      if (!session) {
        state.value.error = `Session ${sessionId} not found`
        return
      }

      session.messages = []
      session.lastActivity = new Date()
      immediateSave()
    }

    const markSessionInactive = (sessionId: string): void => {
      const session = state.value.sessions[sessionId]
      if (!session) {
        return
      }

      session.isActive = false

      if (state.value.activeSessionId === sessionId) {
        state.value.activeSessionId = null
      }

      immediateSave()
    }

    const removeSession = (sessionId: string): void => {
      if (state.value.sessions[sessionId]) {
        delete state.value.sessions[sessionId]

        if (state.value.activeSessionId === sessionId) {
          state.value.activeSessionId = null
        }

        immediateSave()
      }
    }

    const setLoading = (loading: boolean): void => {
      state.value.isLoading = loading
    }

    const setCreatingSession = (creating: boolean): void => {
      state.value.isCreatingSession = creating
    }

    const setTyping = (typing: boolean): void => {
      state.value.isTyping = typing
    }

    const setConnected = (connected: boolean): void => {
      state.value.isConnected = connected
    }

    const setError = (error: string | null): void => {
      state.value.error = error
    }

    const updateConfig = (
      config: Partial<{
        persistMessages: boolean
        autoScrollToBottom: boolean
      }>
    ): void => {
      if (config.persistMessages !== undefined) {
        state.value.persistMessages = config.persistMessages
      }
      if (config.autoScrollToBottom !== undefined) {
        state.value.autoScrollToBottom = config.autoScrollToBottom
      }
    }

    const reset = (): void => {
      state.value.sessions = {}
      state.value.activeSessionId = null

      state.value.isLoading = false
      state.value.isCreatingSession = false
      state.value.isTyping = false
      state.value.isConnected = false
      state.value.error = null
      state.value.lastLoaded = null
      state.value.isInitialized = false
    }

    const clearHistory = (): void => {
      clearLocalStorage(storeKey)
      reset()
    }

    return {
      chatbotId: storeChatbotId,
      sessions,
      activeSessionId,
      isLoading,
      isCreatingSession,
      isTyping,
      isConnected,
      error,
      isInitialized,
      lastLoaded,
      persistMessages,
      autoScrollToBottom,
      sourceDocuments,
      activeSession,
      messages,
      historicalMessages,
      allMessages,
      hasMessages,
      hasHistoricalMessages,
      messageCount,
      lastMessage,
      sessionList,
      setSession,
      setActiveSession,
      addMessageToSession,
      addMessageToActiveSession,
      updateMessageById,
      setSessionMessages,
      clearSessionMessages,
      markSessionInactive,
      removeSession,
      setLoading,
      setCreatingSession,
      setTyping,
      setConnected,
      setError,
      updateConfig,
      reset,
      clearHistory,
      loadPersistedData,
    }
  })
}

export type ChatSessionStore = ReturnType<typeof getOrCreateChatSessionStore>
